<script setup>
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useLookup, useDefaultGridOptions, useDefaultExport, useCurrencyColumnFormatter, useRouteColumnFilter, useUpdateMasterDetail, refreshGridAsync } from '@/composables/data-grid'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import InvoiceDeleteDialog from '@/components/accounting/InvoiceDeleteDialog.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'
import InvoicePrintDialog from '@/components/accounting/InvoicePrintDialog.vue'
import InvoiceRowsGrid from '@/components/accounting/InvoiceRowsGrid.vue'

import { useCompanyColumn, useProjectsColumn } from '@/composables/data-grid-column'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { useDetectDevice } from '@/composables/device'

const props = defineProps({
  projectId: {
    type: Number,
    required: false,
    default: null,
  },
})

const saveStateId = 'invoices-grid-v3'
const gridId = props.projectId ? `${saveStateId}-${props.projectId}` : saveStateId

const i18n = getI18n()
const dataGridWidget = useDataGridWidget()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()

const route = useRoute()
const router = useRouter()
const { gridRef } = useGridRef()
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.invoice', 2) })

const printedInvoice = ref(null)
const editItem = ref(null)
const editStartsOnLines = ref(false)
const invoiceToDelete = ref(null)

planningStore.fetchCompaniesAsync({ show_archived: true }, true).then(() => {
  gridRef.value?.instance.columnOption('company', 'lookup.dataSource', planningStore.companies)
  gridRef.value?.instance.repaint()
})

const gridDataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.invoicesUrl, {
  customParamsFn: () => (props.projectId ? { project: props.projectId } : {}),
})

const localizedStatuses = accountingStore.getLocalizedInvoiceStatuses()

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        hint: i18n.t('common.edit'),
        icon: 'edit',
        onClick: (e) => openEdit(e.row.data),
      },
      {
        hint: i18n.t('common.delete'),
        icon: 'trash',
        onClick: (e) => { invoiceToDelete.value = e.row.data },
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '5rem',
  },
  { dataField: 'invoice_number', caption: i18n.t('common.invoiceNumber'), sortOrder: 'desc', sortIndex: 1 },
  { dataField: 'date', caption: i18n.t('common.date'), dataType: 'date', sortOrder: 'desc', sortIndex: 0 },
  { dataField: 'due_date', caption: i18n.t('common.dueDate'), dataType: 'date' },
  {
    dataField: 'status',
    caption: i18n.t('common.status'),
    lookup: useLookup({ dataSource: localizedStatuses, valueExpr: 'id', displayExpr: 'name' }),
  },
  useCompanyColumn({ validationRules: [], calculateDisplayValue: 'company_name' }),
  useProjectsColumn(),
  ...['total_no_vat', 'total_vat'].map(dataField => ({
    dataField,
    caption: i18n.t(dataField === 'total_vat' ? 'common.totalVat' : 'common.totalNoVat'),
    dataType: 'number',
    allowFiltering: false,
    allowSorting: false,
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, dataField, rowData.currency_ref),
  })),
  {
    dataField: 'currency_ref',
    caption: i18n.t('common.currency'),
    lookup: useLookup({ dataSource: webSiteStore.getLocalizedCurrencies(), valueExpr: 'id', displayExpr: 'name' }),
  },
  { dataField: 'payment_terms', caption: i18n.t('common.paymentTerms'), visible: false },
  { dataField: 'notes', caption: i18n.t('common.notes'), visible: false },
]

const openEdit = (invoice, startOnLines = false) => {
  editStartsOnLines.value = startOnLines
  editItem.value = { ...invoice }
}

const updateGrid = async () => {
  const isCreation = !editItem.value?.id
  editItem.value = null
  if (!isCreation) await gridRef.value.instance.refresh()
}

const onInvoiceDeleted = async () => {
  invoiceToDelete.value = null
  await gridRef.value.instance.refresh()
}

const { onContentReady: onRouteFilterContentReady } = useRouteColumnFilter(gridRef, 'invoice_number')

let highlightingInvoiceId = null
const highlightRouteInvoiceAsync = async () => {
  const invoiceId = Number(route.query.highlight) || null
  if (invoiceId == null || invoiceId === highlightingInvoiceId || !gridRef.value?.instance) return
  highlightingInvoiceId = invoiceId
  const query = { ...route.query }
  delete query.highlight
  router.replace({ query })
  await refreshGridAsync(gridRef, invoiceId)
  highlightingInvoiceId = null
}
watch(() => route.query.highlight, highlightRouteInvoiceAsync)

const onContentReady = (e) => {
  onRouteFilterContentReady(e)
  highlightRouteInvoiceAsync()
}

const gridOptions = useDefaultGridOptions({
  id: gridId,
  saveStateId,
  isDetailGrid: !!props.projectId,
  onContentReady,
  dateSerializationFormat: 'yyyy-MM-ddTHH:mm:ssZ',
  dataSource: gridDataSource,
  columns,
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  export: exportOptions,
  onExporting,
  clearFilterButton: true,
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onSelectionChanged: (e) => dataGridWidget.printWidgetButton.value?.option('disabled', e.selectedRowsData.length !== 1),
  exportWidgets: [dataGridWidget.printWidget({ onClick: () => { printedInvoice.value = gridRef.value.instance.getSelectedRowsData()[0] } })],
  addButton: { onClick: () => openEdit({}) },
  masterDetail: { enabled: true, template: 'master-detail' },
})

const { repaintRow } = useUpdateMasterDetail()
const onInvoiceRowSaved = (invoiceId) => repaintRow(gridRef, accountingStore.fetchInvoiceByIdAsync, invoiceId, invoiceId)
</script>

<template>
  <div>
    <invoice-delete-dialog
      :invoice="invoiceToDelete"
      @deleted="onInvoiceDeleted"
      @cancel="invoiceToDelete = null"
    />
    <q-dialog
      :model-value="editItem !== null"
      class="dialog-xl"
      :maximized="isMobile"
      persistent
    >
      <invoice-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :project-ids="props.projectId && !editItem.id ? [props.projectId] : []"
        :start-on-lines="editStartsOnLines"
        @update="updateGrid"
        @cancel="editItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="printedInvoice !== null" :maximized="isMobile" @hide="printedInvoice = null">
      <invoice-print-dialog
        v-if="printedInvoice !== null"
        :invoice-id="printedInvoice.id"
        @cancel="printedInvoice = null"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <invoice-rows-grid :invoice="templateData.data" @row-saved="onInvoiceRowSaved" @add-rows="(invoice) => openEdit(invoice, true)" />
      </template>
    </dx-data-grid>
  </div>
</template>
